import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Lock, RefreshCw, LogOut, Loader2, Download } from "lucide-react";
import { ADMIN as A } from "@/constants/testIds";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "ch_admin_passcode";

const money = (cents, currency) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: (currency || "usd").toUpperCase() }).format(
    (cents || 0) / 100
  );

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const StatusBadge = ({ status }) => {
  const map = {
    paid: "bg-sage text-white",
    pending: "bg-sand text-ink border border-line",
    initiated: "bg-sand text-ink border border-line",
    failed: "bg-red-100 text-red-800",
    expired: "bg-red-100 text-red-800",
    refunded: "bg-red-100 text-red-800",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold capitalize ${
        map[status] || "bg-sand text-ink border border-line"
      }`}
    >
      {status || "—"}
    </span>
  );
};

export default function AdminOrders() {
  const [authed, setAuthed] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchOrders = useCallback(async (code) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/orders`, {
        headers: { "X-Admin-Passcode": code },
      });
      setOrders(Array.isArray(data) ? data : []);
      setAuthed(true);
      localStorage.setItem(STORAGE_KEY, code);
    } catch (e) {
      if (e?.response?.status === 401) {
        setError("Incorrect passcode. Please try again.");
        setAuthed(false);
        localStorage.removeItem(STORAGE_KEY);
      } else {
        setError("Couldn't load orders. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      setPasscode(saved);
      fetchOrders(saved);
    }
    // Runs once on mount to restore a saved passcode; fetchOrders is stable (useCallback []).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOrders]);

  const handleUnlock = (e) => {
    e.preventDefault();
    if (!passcode.trim()) {
      setError("Please enter the passcode.");
      return;
    }
    fetchOrders(passcode.trim());
  };

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setPasscode("");
    setOrders([]);
  };

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { data } = await axios.get(`${API}/admin/orders/export`, {
        headers: { "X-Admin-Passcode": passcode },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "creator-house-members.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError("Couldn't export the list. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  if (!authed) {
    return (
      <div
        data-testid={A.gate}
        className="font-body bg-cream min-h-screen flex items-center justify-center px-6 text-ink antialiased"
      >
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-sm w-full text-center rounded-2xl border border-line bg-white p-10 shadow-[0_20px_40px_rgb(0,0,0,0.06)]"
        >
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-cream border border-line">
            <Lock className="h-6 w-6 text-clay" aria-hidden="true" />
          </span>
          <p className="mt-6 font-body text-xs font-semibold uppercase tracking-[0.22em] text-clay">
            THE CALLED CREATOR HOUSE
          </p>
          <h1 className="mt-3 font-heading text-3xl tracking-tight text-ink">private dashboard</h1>
          <p className="mt-3 text-sm text-stone-text leading-relaxed">
            Enter your passcode to view everyone who has joined.
          </p>
          <form onSubmit={handleUnlock} className="mt-7 space-y-4">
            <Input
              type="password"
              data-testid={A.passcodeInput}
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Passcode"
              className="bg-white border-line rounded-xl h-12 text-center text-ink placeholder:text-stone-muted focus-visible:ring-clay"
            />
            {error && (
              <p data-testid={A.gateError} className="text-sm text-red-700">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid={A.unlockBtn}
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-sage px-8 py-3.5 text-base font-semibold text-white transition-[transform,background-color] duration-300 hover:bg-sage-dark hover:scale-[1.02] disabled:opacity-70 disabled:hover:scale-100"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : "Unlock"}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  const paidCount = orders.filter((o) => o.payment_status === "paid").length;

  return (
    <div data-testid={A.page} className="font-body bg-cream min-h-screen text-ink antialiased">
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-clay">
              THE CALLED CREATOR HOUSE
            </p>
            <h1 className="mt-2 font-heading text-4xl sm:text-5xl tracking-tight text-ink">who's joined</h1>
            <p className="mt-3 text-base text-stone-text">
              {paidCount} paid · {orders.length} total
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid={A.exportBtn}
              onClick={handleExport}
              disabled={exporting || orders.length === 0}
              className="inline-flex items-center gap-2 rounded-full bg-sage px-5 py-2.5 text-sm font-semibold text-white transition-[transform,background-color] hover:bg-sage-dark hover:scale-[1.03] disabled:opacity-60 disabled:hover:scale-100"
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Download className="h-4 w-4" aria-hidden="true" />
              )}
              Export CSV
            </button>
            <button
              data-testid={A.refresh}
              onClick={() => fetchOrders(passcode)}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:bg-white/70 disabled:opacity-70"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
              Refresh
            </button>
            <button
              data-testid={A.logout}
              onClick={handleLogout}
              className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:bg-white/70"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              Lock
            </button>
          </div>
        </div>

        {error && <p className="mt-6 text-sm text-red-700">{error}</p>}

        <div className="mt-10 overflow-x-auto rounded-2xl border border-line bg-white">
          {orders.length === 0 ? (
            <p data-testid={A.empty} className="p-12 text-center text-stone-text">
              No one has joined yet.
            </p>
          ) : (
            <table data-testid={A.table} className="w-full min-w-[640px] text-left">
              <thead>
                <tr className="border-b border-line">
                  {["First name", "Email", "Amount", "Purchased", "Status"].map((h) => (
                    <th
                      key={h}
                      className="px-6 py-4 font-body text-xs font-semibold uppercase tracking-[0.14em] text-stone-muted"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.map((o, i) => (
                  <tr key={o.session_id || `${o.email}-${o.created_at}`} data-testid={A.row(i)} className="border-b border-line last:border-0">
                    <td className="px-6 py-5 font-heading text-lg text-ink">{o.first_name || "—"}</td>
                    <td className="px-6 py-5 text-sm text-stone-text">{o.email || "—"}</td>
                    <td className="px-6 py-5 text-sm font-semibold text-ink">{money(o.amount, o.currency)}</td>
                    <td className="px-6 py-5 text-sm text-stone-text">{formatDate(o.created_at)}</td>
                    <td className="px-6 py-5">
                      <StatusBadge status={o.payment_status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
